import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Modal, Popconfirm, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, CaretRightOutlined } from "@ant-design/icons";
import {
    IUDMConfig,
    IUDMOperand,
    SCOPE_OPTIONS,
    STAT_OPTIONS,
    VARIABLE_LABEL_OVERRIDES,
} from "./constants";

const { Text } = Typography;

const SCOPE_LABELS: Record<string, string> = Object.fromEntries(SCOPE_OPTIONS.map((o) => [o.value, o.label]));
const STAT_LABELS: Record<string, string> = Object.fromEntries(STAT_OPTIONS.map((o) => [o.value, o.label]));
const VARIABLE_LABELS: Record<string, string> = {
    latest: "Current date value",
    min: "All Time Minimum",
    max: "All Time Maximum",
    hist_avg: "Historical Avg",
    hist_sd: "Historical Standard Deviation",
    ...VARIABLE_LABEL_OVERRIDES,
};

interface Operand {
    name: string;
    metric: string;
    scope: string;
    agg: string;
    variable: string;
    // Only the first argument of value_to_hit_acwr: the help shows the metric alone.
    metricOnly?: boolean;
}

interface Example {
    formula: string;
    // Editor form with bare opN tokens, for examples the quoted-name replacement
    // cannot express (a query condition keeps its quotes and holds bare
    // opN tokens inside). Defaults to name→opN replacement on `formula`.
    builderFormula?: string;
    meaning: string;
    operands: Operand[];
}

const EXAMPLES: Example[] = [
    {
        formula: `"Player Load" / "Minutes Played"`,
        meaning: "Player load accrued per minute on the pitch — a load-intensity metric",
        operands: [
            { name: "Player Load", metric: "Player Load", scope: "current:player", agg: "sum", variable: "latest" },
            { name: "Minutes Played", metric: "Minutes Played", scope: "current:player", agg: "sum", variable: "latest" },
        ],
    },
    {
        formula: `"Player Distance" / "Team Avg Distance" * 100`,
        meaning: "Today's distance as a percentage of the team's average distance",
        operands: [
            { name: "Player Distance", metric: "Distance", scope: "current:player", agg: "sum", variable: "latest" },
            { name: "Team Avg Distance", metric: "Distance", scope: "current:full-team", agg: "avg", variable: "latest" },
        ],
    },
    {
        formula: `"Top Speed" / "ATH Top Speed" * 100`,
        meaning: "Today's top speed as a percentage of the player's all-time best",
        operands: [
            { name: "Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "latest" },
            { name: "ATH Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "max" },
        ],
    },
    {
        formula: `("Pre-Train Wt" - "Post-Train Wt") / "Pre-Train Wt" * 100`,
        meaning: "Percentage of body weight lost during today's session",
        operands: [
            { name: "Pre-Train Wt", metric: "Pre-Training Weight", scope: "current:player", agg: "avg", variable: "latest" },
            { name: "Post-Train Wt", metric: "Post-Training Weight", scope: "current:player", agg: "avg", variable: "latest" },
        ],
    },
    {
        formula: `("Player Load" - "Hist Avg Load") / "Hist SD Load"`,
        meaning: "How far today's player load sits from the player's historical norm, in standard deviations",
        operands: [
            { name: "Player Load", metric: "Player Load", scope: "current:player", agg: "sum", variable: "latest" },
            { name: "Hist Avg Load", metric: "Player Load", scope: "current:player", agg: "sum", variable: "hist_avg" },
            { name: "Hist SD Load", metric: "Player Load", scope: "current:player", agg: "sum", variable: "hist_sd" },
        ],
    },
    {
        formula: `days_since("Top Speed >= 0.9 * ATH Top Speed", 30)`,
        builderFormula: `days_since("op1 >= 0.9 * op2", 30)`,
        meaning: "Days since the player last reached 90% of their all-time top speed, looking back 30 days",
        operands: [
            { name: "Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "latest" },
            { name: "ATH Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "max" },
        ],
    },
    {
        formula: `count_true("Top Speed >= 0.9 * ATH Top Speed", 30)`,
        builderFormula: `count_true("op1 >= 0.9 * op2", 30)`,
        meaning: "How many days the player reached 90% of their all-time top speed in the last 30 days",
        operands: [
            { name: "Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "latest" },
            { name: "ATH Top Speed", metric: "Top Speed", scope: "current:player", agg: "best", variable: "max" },
        ],
    },
    {
        formula: `value_to_hit_acwr("High Intensity Load", 1.5)`,
        meaning: "Today's High Intensity Load that puts tomorrow's ACWR at 1.5. Use 0.8 for the daily minimum and 1.5 for the daily maximum.",
        operands: [
            { name: "High Intensity Load", metric: "High Intensity Load", scope: "current:player", agg: "sum", variable: "latest", metricOnly: true },
        ],
    },
    {
        formula: `value_to_hit_acwr("High Intensity Load", 1.5) / (32 * "HI Load Baseline")`,
        meaning: "The same value in Game Units: 32 game minutes times the player's per-minute game mean.",
        operands: [
            { name: "High Intensity Load", metric: "High Intensity Load", scope: "current:player", agg: "sum", variable: "latest", metricOnly: true },
            { name: "HI Load Baseline", metric: "High Intensity Load", scope: "current:player", agg: "sum", variable: "metric_baseline" },
        ],
    },
];

// Turn an example (quoted names) into a builder IUDMConfig: each unique operand becomes opN,
// scope/variable are carried over, but metric_identifier is left blank for the user to pick.
const exampleToConfig = (ex: Example): IUDMConfig => {
    let formula = ex.builderFormula ?? ex.formula;
    const fetchParams: Record<string, IUDMOperand> = {};
    ex.operands.forEach((op, idx) => {
        const opName = `op${idx + 1}`;
        if (!ex.builderFormula) formula = formula.split(`"${op.name}"`).join(opName);
        fetchParams[opName] = { scope: op.scope, variable: op.variable, metric_identifier: "", label: op.name };
    });
    return { formula, fetch_params: fetchParams };
};

interface FormulaHelpModalProps {
    onApply: (config: IUDMConfig) => void;
}

// Share of the collapsed content height the dialog is pinned to. 1 fits that content
// exactly, so scrolling only starts once an example is expanded; below 1 trims the
// dialog and leaves the last rows under the fold.
const BODY_HEIGHT_RATIO = 1;

const FormulaHelpModal: React.FC<FormulaHelpModalProps> = ({ onApply }) => {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [bodyHeight, setBodyHeight] = useState<number | undefined>(undefined);
    const bodyRef = useRef<HTMLDivElement>(null);
    const scrollToBottom = useRef(false);

    // Reset on close, so the next open measures a collapsed body again.
    useEffect(() => {
        if (open) return;
        setExpanded(new Set());
        setBodyHeight(undefined);
    }, [open]);

    // Pin the body to the height it has with every example collapsed (the CSS caps that
    // against the viewport). Expanding a row then scrolls inside the body rather than
    // growing the dialog toward the bottom of the window.
    useLayoutEffect(() => {
        if (!open || bodyHeight !== undefined || !bodyRef.current) return;
        const collapsedHeight = bodyRef.current.scrollHeight;
        if (collapsedHeight > 0) setBodyHeight(Math.round(collapsedHeight * BODY_HEIGHT_RATIO));
    }, [open, bodyHeight]);

    // A row's detail lands below the fold once the height is pinned, so bring it into view.
    useEffect(() => {
        if (!scrollToBottom.current || !bodyRef.current) return;
        scrollToBottom.current = false;
        bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
    }, [expanded]);

    const toggle = (i: number) => {
        const willExpand = !expanded.has(i);
        scrollToBottom.current = willExpand;
        setExpanded((prev) => {
            const next = new Set(prev);
            if (willExpand) next.add(i);
            else next.delete(i);
            return next;
        });
    };

    const applyExample = (ex: Example) => {
        onApply(exampleToConfig(ex));
        setOpen(false);
    };

    return (
        <>
            <Tooltip title="How to create a formula">
                <InfoCircleOutlined
                    className="udm-condition-help-icon"
                    onClick={() => setOpen(true)}
                />
            </Tooltip>
            <Modal
                title="How to create a formula"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={1080}
            >
                <div
                    className="udm-condition-help"
                    ref={bodyRef}
                    style={bodyHeight === undefined ? undefined : { height: bodyHeight }}
                >
                    <p>A formula computes a value from your data. You build it from your own data streams and simple math.</p>

                    <ol>
                        <li>
                            <b>Name your values</b> in quotes, like <Text code>"Player Load"</Text>. The name is
                            just a quick way to identify the value. You choose what it actually measures (the metric,
                            scope, and variable) in the block that appears below for each one.
                        </li>
                        <li>
                            <b>Do math</b> with <Text code>+</Text> <Text code>-</Text>{" "}
                            <Text code>*</Text> <Text code>/</Text> and <Text code>( )</Text> to combine values into
                            the number you want.
                        </li>
                        <li>
                            <b>Use constants</b> (optional) like <Text code>* 100</Text> to turn a ratio into a
                            percentage.
                        </li>
                        <li>
                            <b>Count days</b> (optional) with <Text code>days_since(...)</Text> or{" "}
                            <Text code>count_true(...)</Text>, or find the value that hits a target ACWR with{" "}
                            <Text code>value_to_hit_acwr(...)</Text> — see below.
                        </li>
                    </ol>

                    <div className="udm-condition-help-function">
                        <div className="udm-condition-help-function-title">
                            days_since — how long since something last happened
                        </div>
                        <p>
                            <Text code>days_since("Player Load &gt;= 0.9 * Hist Avg Load", 30)</Text> — days since
                            this player last hit 90% of their historical average load, checking back 30 days.
                        </p>
                        <ul>
                            <li>
                                <b>Condition</b>, in quotes. Type value names bare and pick them from the list.
                                Compare with <Text code>&gt; &lt; &gt;= &lt;= == !=</Text>, do math with{" "}
                                <Text code>+ - * /</Text>, join with <Text code>and</Text> / <Text code>or</Text>,
                                use <Text code>max()</Text> / <Text code>min()</Text>.
                            </li>
                            <li>
                                <b>Lookback</b>, 1 to 90 days — counted back from each day, not from today.
                            </li>
                            <li>
                                <b>Result</b>: 0 when the condition is true on the day itself, otherwise the days
                                back to the most recent true day — blank when it was never true in the window. A
                                day missing one of the condition's values can't be the match.
                            </li>
                        </ul>
                    </div>

                    <div className="udm-condition-help-function">
                        <div className="udm-condition-help-function-title">
                            count_true — how often something happened
                        </div>
                        <p>
                            <Text code>count_true("Player Load &gt;= 0.9 * Hist Avg Load", 30)</Text> — how many
                            days this player hit 90% of their historical average load, checking back 30 days.
                        </p>
                        <ul>
                            <li>
                                <b>Condition</b> and <b>lookback</b> work exactly like <Text code>days_since</Text>.
                            </li>
                            <li>
                                <b>Result</b>: the number of days in the window where the condition was true,
                                counting the day itself — 0 when it was never true. Blank when no day in the
                                window has the condition's values. A day missing one of them can't count.
                            </li>
                        </ul>
                    </div>

                    <div className="udm-condition-help-function">
                        <div className="udm-condition-help-function-title">
                            value_to_hit_acwr — the value that lands a target ACWR
                        </div>
                        <p>
                            <Text code>value_to_hit_acwr("High Intensity Load", 1.5)</Text> — today's value of
                            that metric that puts tomorrow's ACWR at 1.5.
                        </p>
                        <ul>
                            <li>
                                <b>Arguments</b>: a GPS metric that has an ACWR, then the target, like 0.8 or 1.5.
                            </li>
                            <li>
                                <b>Result</b>: reads data through yesterday, so it has a value each morning. 0 means
                                the ACWR stays at or above the target even with rest.
                            </li>
                            <li>
                                <b>Limit</b>: the target must stay below the team's maximum ACWR, for example 4 for a
                                7 / 28-day rolling team.
                            </li>
                        </ul>
                    </div>

                    <table className="udm-condition-help-examples">
                        <thead>
                            <tr>
                                <th>Formula</th>
                                <th>What it computes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {EXAMPLES.map((ex, i) => {
                                const isOpen = expanded.has(i);
                                return (
                                    <React.Fragment key={ex.formula}>
                                        <tr className="udm-example-row" onClick={() => toggle(i)}>
                                            <td>
                                                <div className="udm-example-cond-cell">
                                                    <span className="udm-example-cond">
                                                        <CaretRightOutlined
                                                            className="udm-example-caret"
                                                            rotate={isOpen ? 90 : 0}
                                                        />
                                                        <code>{ex.formula}</code>
                                                    </span>
                                                    <Popconfirm
                                                        title="Use this formula?"
                                                        description={
                                                            <span>
                                                                This replaces your current formula. Since it's just
                                                                an example, you'll need to pick a matching metric from
                                                                your list for each value. If one isn't on your account,
                                                                choose an alternative.
                                                            </span>
                                                        }
                                                        okText="Yes, use it"
                                                        cancelText="Cancel"
                                                        overlayStyle={{ maxWidth: 320 }}
                                                        onConfirm={() => applyExample(ex)}
                                                    >
                                                        <Tag
                                                            color="blue"
                                                            className="udm-example-use"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            Use this formula
                                                        </Tag>
                                                    </Popconfirm>
                                                </div>
                                            </td>
                                            <td>{ex.meaning}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="udm-example-detail">
                                                <td colSpan={2}>
                                                    {ex.operands.map((op) => (
                                                        <div key={op.name} className="udm-operand-detail">
                                                            <div className="udm-operand-name">{op.name}</div>
                                                            <div className="udm-operand-config">Metric = {op.metric}</div>
                                                            {op.metricOnly ? (
                                                                <div className="udm-operand-config">value_to_hit_acwr uses only the metric</div>
                                                            ) : (
                                                                <>
                                                                    <div className="udm-operand-config">Scope = {SCOPE_LABELS[op.scope] ?? op.scope}</div>
                                                                    <div className="udm-operand-config">Agg = {STAT_LABELS[op.agg] ?? op.agg}</div>
                                                                    <div className="udm-operand-config">Variable = {VARIABLE_LABELS[op.variable] ?? op.variable}</div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </>
    );
};

export default FormulaHelpModal;
